import { useCallback, useEffect, useRef, useState } from 'react';
import { synthesiseOfdmTransmission, type AudioCarrierPlan } from '@spectrabus/audio-modem';
import {
  ReceiverLiveSession,
  SenderLiveSession,
  type LiveObjectInput,
} from '@spectrabus/live-engine';
import type { LinkSnapshot } from '@spectrabus/telemetry';
import { createForwardAudioPlan, createReturnAudioPlan } from './audio-profile';
import { decodeReturnText, encodeReturnText, extractBootstrapText } from './bootstrap-link';
import type { CaptureRuntimeCommand, CaptureRuntimeEvent } from './capture-runtime';
import { buildReceiverLiveSnapshot, buildSenderLiveSnapshot } from './live-snapshot';
import receiverAudioWorkletUrl from './receiver-audio.worklet.ts?worker&url';

const OPTICAL_PRESENT_INTERVAL_MS = 280;
const SNAPSHOT_INTERVAL_MS = 400;
const ACOUSTIC_GAP_MS = 350;
const RETURN_GAP_MS = 250;
const RETURN_COMPLETE_GAP_MS = 2_000;
const QR_SCAN_INTERVAL_MS = 650;
const OPTICAL_SAMPLE_COLD_MS = 420;
const OPTICAL_SAMPLE_TRACKED_MS = 120;
const SESSION_DISCOVERY_OFFER_BYTES = 141;

function audioSessionApi(): { type: string } | undefined {
  return (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
}

// A duplex peer holds the microphone open while its speaker emits, which on
// platforms with the Audio Session API demands the combined session type —
// at the cost of output routed to the quiet earpiece on phones. A peer that
// only plays declares plain playback and keeps the loudspeaker.
function requestAudioSessionType(type: 'playback' | 'play-and-record'): void {
  const audioSession = audioSessionApi();
  if (audioSession !== undefined) audioSession.type = type;
}

function sleep(durationMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, durationMs));
}

interface DiagnosticsLog {
  note(line: string): void;
  toClipboard(role: string, summary: Record<string, unknown>): Promise<boolean>;
}

/**
 * A bounded in-memory event log the operator can copy as JSON. The
 * applications deliberately open no network path, so shared debugging works
 * by explicit export: press the button, paste the log to whoever is
 * diagnosing. Nothing leaves the device on its own.
 */
function createDiagnosticsLog(): DiagnosticsLog {
  const entries: string[] = [];
  return {
    note(line: string): void {
      entries.push(`${new Date().toISOString().slice(11, 23)} ${line}`);
      if (entries.length > 400) entries.splice(0, entries.length - 400);
    },
    async toClipboard(role: string, summary: Record<string, unknown>): Promise<boolean> {
      const payload = JSON.stringify({ role, capturedAt: new Date().toISOString(), summary, log: entries }, null, 1);
      try {
        await navigator.clipboard.writeText(payload);
        return true;
      } catch {
        return false;
      }
    },
  };
}

async function playSamples(context: AudioContext, samples: Float32Array): Promise<void> {
  // Mobile platforms suspend an idle context between bursts; starting a
  // source on a suspended context plays silence and never fires onended,
  // which would hang the emission loop forever. Resume first, and resolve
  // on a duration-based fallback even if the completion event never comes.
  if (context.state !== 'running') {
    await context.resume().catch(() => undefined);
  }
  const buffer = context.createBuffer(1, samples.length, context.sampleRate);
  // Copied into a fresh backing store: copyToChannel requires a plain
  // ArrayBuffer view and the modem may hand back a differently backed array.
  buffer.copyToChannel(new Float32Array(samples), 0);
  const source = context.createBufferSource();
  source.buffer = buffer;
  const gain = context.createGain();
  gain.gain.value = 0.75;
  source.connect(gain);
  gain.connect(context.destination);
  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      source.disconnect();
      gain.disconnect();
      resolve();
    };
    const durationMs = (samples.length / context.sampleRate) * 1_000;
    const fallback = window.setTimeout(finish, durationMs + 1_500);
    source.onended = () => {
      window.clearTimeout(fallback);
      finish();
    };
    source.start(context.currentTime + 0.02);
  });
}

interface CaptureAudioGraph {
  readonly context: AudioContext;
  readonly worker: Worker;
  release(): void;
}

/**
 * Opens the microphone into the streaming burst decoder for one duplex band.
 * The worklet forwards raw blocks to a worker that owns the demodulator, so
 * the render thread never touches DSP.
 */
async function openCaptureAudioGraph(
  stream: MediaStream,
  band: 'forward' | 'return',
  workerName: string,
  onBurst: (payload: Uint8Array) => void,
  onRejected?: () => void,
): Promise<CaptureAudioGraph> {
  const context = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' });
  await context.resume();
  if (context.sampleRate !== 48_000) {
    // The duplex band plans are defined on the canonical 48 kHz grid and
    // both peers must derive identical carrier frequencies without
    // negotiation. A platform that refuses the requested rate would
    // demodulate against shifted bins and fail silently; failing loudly
    // here names the actual cause instead.
    void context.close().catch(() => undefined);
    throw new Error(
      `This device's audio engine runs at ${context.sampleRate} Hz, but the duplex carrier grid requires 48000 Hz.`,
    );
  }
  await context.audioWorklet.addModule(receiverAudioWorkletUrl);
  const worker = new Worker(new URL('./capture-runtime.worker.ts', import.meta.url), {
    name: workerName,
    type: 'module',
  });
  worker.onmessage = (event: MessageEvent<CaptureRuntimeEvent>) => {
    if (event.data.type === 'audio-burst') {
      onBurst(new Uint8Array(event.data.payload));
      return;
    }
    if (event.data.type === 'audio-rejected') {
      // A rejected burst still proves acoustic energy reached the
      // demodulator — the distinction an operator needs when nothing locks.
      onRejected?.();
    }
  };
  const initialise: CaptureRuntimeCommand = {
    type: 'audio-initialise',
    sampleRate: context.sampleRate,
    band,
  };
  worker.postMessage(initialise);
  const source = context.createMediaStreamSource(new MediaStream(stream.getAudioTracks()));
  const node = new AudioWorkletNode(context, 'spectrabus-capture');
  node.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
    const command: CaptureRuntimeCommand = { type: 'audio-block', samples: event.data };
    worker.postMessage(command, [event.data]);
  };
  // The worklet needs a destination connection to keep processing; the gain
  // stays at zero so the microphone is never monitored audibly.
  const silent = context.createGain();
  silent.gain.value = 0;
  source.connect(node);
  node.connect(silent);
  silent.connect(context.destination);
  return {
    context,
    worker,
    release: () => {
      worker.postMessage({ type: 'reset' } satisfies CaptureRuntimeCommand);
      worker.terminate();
      node.port.onmessage = null;
      source.disconnect();
      node.disconnect();
      silent.disconnect();
      void context.close().catch(() => undefined);
    },
  };
}

function decodeBootstrapText(value: string): Uint8Array | null {
  const bootstrapText = extractBootstrapText(value);
  if (bootstrapText === null) return null;
  const encoded = bootstrapText.slice(4);
  if (encoded === '' || !/^[A-Za-z\d_-]+$/u.test(encoded)) return null;
  const paddingLength = (4 - (encoded.length % 4)) % 4;
  if (paddingLength === 3) return null;
  try {
    const binary = atob(
      `${encoded.replace(/-/gu, '+').replace(/_/gu, '/')}${'='.repeat(paddingLength)}`,
    );
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return bytes.length === SESSION_DISCOVERY_OFFER_BYTES ? bytes : null;
  } catch {
    return null;
  }
}

interface DetectedBarcode {
  readonly rawValue: string;
}

interface BarcodeDetectorInstance {
  detect(source: HTMLVideoElement): Promise<readonly DetectedBarcode[]>;
}

interface BarcodeDetectorConstructor {
  new (options?: { readonly formats?: readonly string[] }): BarcodeDetectorInstance;
  getSupportedFormats?(): Promise<readonly string[]>;
}

async function createQrDetector(): Promise<BarcodeDetectorInstance | null> {
  const constructor = (globalThis as typeof globalThis & {
    readonly BarcodeDetector?: BarcodeDetectorConstructor;
  }).BarcodeDetector;
  if (constructor === undefined) return null;
  try {
    const formats = (await constructor.getSupportedFormats?.()) ?? [];
    if (formats.length > 0 && !formats.includes('qr_code')) return null;
    return new constructor({ formats: ['qr_code'] });
  } catch {
    return null;
  }
}

export type LiveSenderState = 'idle' | 'starting' | 'running' | 'failed';

export interface LiveSenderController {
  readonly state: LiveSenderState;
  readonly failureMessage: string | null;
  readonly snapshot: LinkSnapshot | null;
  /**
   * Camera preview for the optical return path, when this platform can scan
   * the receiver's return QR; null when the return path is acoustic-only.
   */
  readonly returnVideoElement: HTMLVideoElement | null;
  begin(input: LiveObjectInput): Promise<void>;
  /**
   * Plays a return-band test burst through this device's own speaker. The
   * microphone chain hearing it proves the entire local return listener
   * (capture, band filter, demodulation, engine) end to end: the engine
   * rejects the test payload, so PROTOCOL-REJECTED ticking up is the pass
   * signal. Silence across all counters localises the fault to this
   * device's capture path rather than the peer's emission.
   */
  runReturnLoopback(): Promise<void>;
  /** Copies the bounded diagnostics log as JSON to the clipboard. */
  copyDiagnostics(): Promise<boolean>;
  reset(): void;
}

export function useLiveSender(enabled: boolean): LiveSenderController {
  const [state, setState] = useState<LiveSenderState>('idle');
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<LinkSnapshot | null>(null);
  const [returnVideoElement, setReturnVideoElement] = useState<HTMLVideoElement | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);
  const loopbackRef = useRef<(() => Promise<void>) | null>(null);
  const diagnosticsRef = useRef<DiagnosticsLog>(createDiagnosticsLog());
  const engineSummaryRef = useRef<(() => Record<string, unknown>) | null>(null);
  const beginRunRef = useRef(0);

  const reset = useCallback(() => {
    beginRunRef.current += 1;
    releaseRef.current?.();
    releaseRef.current = null;
    loopbackRef.current = null;
    setSnapshot(null);
    setFailureMessage(null);
    setReturnVideoElement(null);
    setState('idle');
  }, []);

  useEffect(
    () => () => {
      beginRunRef.current += 1;
      releaseRef.current?.();
    },
    [],
  );

  const begin = useCallback(
    async (input: LiveObjectInput) => {
      if (!enabled || releaseRef.current !== null) return;
      const beginRun = (beginRunRef.current += 1);
      setState('starting');
      setFailureMessage(null);
      let stream: MediaStream | null = null;
      let audioGraph: CaptureAudioGraph | null = null;
      let stopped = false;
      const timers: number[] = [];
      try {
        const engine = await SenderLiveSession.create(input);
        requestAudioSessionType('play-and-record');
        // The sender's microphone is the return-band receiver: the speaker
        // and display carry forward traffic while the mic closes the loop.
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { autoGainControl: false, echoCancellation: false, noiseSuppression: false },
        });
        if (beginRunRef.current !== beginRun) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        let returnPhyRejections = 0;
        const diagnostics = diagnosticsRef.current;
        diagnostics.note(`sender session ${engine.sessionId.slice(0, 8)} created`);
        engineSummaryRef.current = () => ({
          phase: engine.phase,
          phaseDetail: engine.phaseDetail,
          ledger: { ...engine.ledger },
          returnPhyRejections,
          integrityViolations: engine.integrityViolations,
        });
        audioGraph = await openCaptureAudioGraph(
          stream,
          'return',
          'spectrabus-sender-return-capture',
          (payload) => {
            void engine.acceptReturnPayload(payload).then((events) => {
              for (const event of events) diagnostics.note(`return audio: ${event.kind} ${event.detail}`);
            });
          },
          () => {
            returnPhyRejections += 1;
            diagnostics.note('return audio: PHY rejected burst');
          },
        );
        const context = audioGraph.context;
        const forwardPlan: AudioCarrierPlan = createForwardAudioPlan(context.sampleRate);
        const publish = (): void => {
          setSnapshot(
            buildSenderLiveSnapshot(engine, forwardPlan, {
              opticalBytesPerSecond: 0,
              acousticBytesPerSecond: 0,
              decoderLatencyMs: null,
              cameraFps: null,
              displayCadenceHz: 1_000 / OPTICAL_PRESENT_INTERVAL_MS,
              returnBurstFailures: returnPhyRejections,
            }),
          );
        };

        let opticalBusy = false;
        timers.push(
          window.setInterval(() => {
            if (opticalBusy || stopped) return;
            opticalBusy = true;
            void engine
              .nextOpticalFrame()
              .catch(() => null)
              .finally(() => {
                opticalBusy = false;
              });
          }, OPTICAL_PRESENT_INTERVAL_MS),
        );
        timers.push(
          window.setInterval(() => {
            if (stopped) return;
            engine.tick();
            publish();
          }, SNAPSHOT_INTERVAL_MS),
        );
        void (async () => {
          while (!stopped) {
            if (engine.phase === 'COMPLETE' || engine.phase === 'FAILED') {
              await sleep(1_000);
              continue;
            }
            let payload: Uint8Array | null;
            try {
              payload = await engine.nextAcousticPayload();
            } catch {
              payload = null;
            }
            if (payload === null) {
              await sleep(600);
              continue;
            }
            const transmission = synthesiseOfdmTransmission(payload, forwardPlan);
            await playSamples(context, transmission.samples);
            await sleep(ACOUSTIC_GAP_MS);
          }
        })();

        // Optical return path: where this platform can detect QR codes, the
        // camera watches for the receiver's `SB1R:` return frames. This is
        // the graceful degradation the physical link needs — either medium
        // may carry the return direction, and the engine cannot tell them
        // apart. Denied camera access or a missing detector simply leaves
        // the return path acoustic-only.
        let returnCameraStream: MediaStream | null = null;
        let returnVideo: HTMLVideoElement | null = null;
        try {
          const detector = await createQrDetector();
          if (detector !== null) {
            returnCameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (beginRunRef.current !== beginRun) {
              for (const track of returnCameraStream.getTracks()) track.stop();
              returnCameraStream = null;
            } else {
              const video = document.createElement('video');
              video.muted = true;
              video.playsInline = true;
              video.setAttribute('aria-label', 'Sender return-path camera preview');
              video.srcObject = returnCameraStream;
              const facing = returnCameraStream.getVideoTracks()[0]?.getSettings().facingMode;
              if (facing !== 'environment') video.style.transform = 'scaleX(-1)';
              await video.play();
              returnVideo = video;
              let scanBusy = false;
              timers.push(
                window.setInterval(() => {
                  if (stopped || scanBusy) return;
                  scanBusy = true;
                  void detector
                    .detect(video)
                    .then(async (barcodes) => {
                      for (const barcode of barcodes) {
                        const payload = decodeReturnText(barcode.rawValue);
                        if (payload !== null) {
                          const events = await engine.acceptReturnPayload(payload);
                          for (const event of events) diagnostics.note(`return QR: ${event.kind} ${event.detail}`);
                          publish();
                        }
                      }
                    })
                    .catch(() => undefined)
                    .finally(() => {
                      scanBusy = false;
                    });
                }, 500),
              );
              setReturnVideoElement(video);
            }
          }
        } catch {
          returnVideo = null;
        }
        const cameraToStop = returnCameraStream;
        const videoToDetach = returnVideo;
        releaseRef.current = () => {
          stopped = true;
          for (const timer of timers) window.clearInterval(timer);
          audioGraph?.release();
          for (const track of stream?.getTracks() ?? []) track.stop();
          for (const track of cameraToStop?.getTracks() ?? []) track.stop();
          if (videoToDetach !== null) videoToDetach.srcObject = null;
        };
        const returnPlan = createReturnAudioPlan(context.sampleRate);
        loopbackRef.current = async () => {
          if (stopped) return;
          const transmission = synthesiseOfdmTransmission(engine.offerBytes, returnPlan);
          await playSamples(context, transmission.samples);
        };
        publish();
        setState('running');
      } catch (error) {
        stopped = true;
        for (const timer of timers) window.clearInterval(timer);
        audioGraph?.release();
        for (const track of stream?.getTracks() ?? []) track.stop();
        releaseRef.current = null;
        setFailureMessage(error instanceof Error ? error.message : 'The live sender could not start.');
        setState('idle');
        // Rethrown so the ingress surface that invoked the transfer can
        // present the failure beside the affordance that caused it.
        throw error;
      }
    },
    [enabled],
  );

  const runReturnLoopback = useCallback(async () => {
    diagnosticsRef.current.note('loopback: playing return-band test burst');
    await loopbackRef.current?.();
  }, []);

  const copyDiagnostics = useCallback(
    async () => diagnosticsRef.current.toClipboard('sender', engineSummaryRef.current?.() ?? {}),
    [],
  );

  return {
    state,
    failureMessage,
    snapshot,
    returnVideoElement,
    begin,
    runReturnLoopback,
    copyDiagnostics,
    reset,
  };
}

export type LiveReceiverState = 'idle' | 'opening' | 'scanning' | 'session' | 'failed';

export interface LiveReceiverController {
  readonly state: LiveReceiverState;
  readonly failureMessage: string | null;
  readonly snapshot: LinkSnapshot | null;
  readonly statusDetail: string;
  readonly cameraFps: number | null;
  readonly videoElement: HTMLVideoElement | null;
  /** True when forward-audio reception is traded for loudspeaker output. */
  readonly speakerPriority: boolean;
  /**
   * The current return payload as `SB1R:` text for the optical return path:
   * rendered as a QR the sender's camera can scan, carrying exactly the
   * bytes the acoustic return band carries.
   */
  readonly returnQrText: string | null;
  /** Which way the active camera faces; null before the aperture opens. */
  readonly cameraFacing: 'environment' | 'user' | null;
  open(): Promise<void>;
  /**
   * Swaps between the rear and front camera. The front camera faces the
   * same way as this display, which is the only geometry where one phone
   * can watch the sender's raster and show its return QR simultaneously.
   */
  switchCamera(): Promise<void>;
  /** Copies the bounded diagnostics log as JSON to the clipboard. */
  copyDiagnostics(): Promise<boolean>;
  reset(): void;
}

export function useLiveReceiver(enabled: boolean): LiveReceiverController {
  const [state, setState] = useState<LiveReceiverState>('idle');
  const [failureMessage, setFailureMessage] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<LinkSnapshot | null>(null);
  const [statusDetail, setStatusDetail] = useState(() =>
    typeof location !== 'undefined' && extractBootstrapText(location.hash.slice(1)) !== null
      ? 'Sender offer received from the scanned link — open the receiver to answer physically.'
      : 'Camera and microphone remain closed until requested.',
  );
  const [cameraFps, setCameraFps] = useState<number | null>(null);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [speakerPriority, setSpeakerPriority] = useState(false);
  const [returnQrText, setReturnQrText] = useState<string | null>(null);
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user' | null>(null);
  const releaseRef = useRef<(() => void) | null>(null);
  const switchCameraRef = useRef<(() => Promise<void>) | null>(null);
  const diagnosticsRef = useRef<DiagnosticsLog>(createDiagnosticsLog());
  const engineSummaryRef = useRef<(() => Record<string, unknown>) | null>(null);
  // A monotonically increasing run identifier lets a reset issued while the
  // hardware prompt is still pending disown the in-flight open: whatever it
  // eventually acquires is released instead of resurrecting a dead session.
  const openRunRef = useRef(0);

  const reset = useCallback(() => {
    openRunRef.current += 1;
    releaseRef.current?.();
    releaseRef.current = null;
    setSnapshot(null);
    setFailureMessage(null);
    setCameraFps(null);
    setVideoElement(null);
    setSpeakerPriority(false);
    setReturnQrText(null);
    setCameraFacing(null);
    switchCameraRef.current = null;
    engineSummaryRef.current = null;
    setStatusDetail('Camera and microphone remain closed until requested.');
    setState('idle');
  }, []);

  useEffect(
    () => () => {
      openRunRef.current += 1;
      releaseRef.current?.();
    },
    [],
  );

  const open = useCallback(async () => {
    if (!enabled || releaseRef.current !== null) return;
    const openRun = (openRunRef.current += 1);
    setState('opening');
    setFailureMessage(null);
    setStatusDetail('Requesting camera and microphone…');
    let stream: MediaStream | null = null;
    let audioGraph: CaptureAudioGraph | null = null;
    let opticalWorker: Worker | null = null;
    let stopped = false;
    const timers: number[] = [];
    let standalonePlayback: AudioContext | null = null;
    try {
      // Speaker priority: on platforms with the Audio Session API (iOS),
      // holding the microphone open forces the combined session type and
      // routes output to the quiet earpiece — exactly where the return band
      // dies. The receiver there gives up forward-audio reception (the
      // raster carries every forward message anyway) to keep the
      // loudspeaker for its return emission.
      const speakerPriority = audioSessionApi() !== undefined;
      requestAudioSessionType(speakerPriority ? 'playback' : 'play-and-record');
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 60 },
        },
        ...(speakerPriority
          ? {}
          : { audio: { autoGainControl: false, echoCancellation: false, noiseSuppression: false } }),
      });
      if (openRunRef.current !== openRun) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }

      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('aria-label', 'Receiver camera preview');
      video.srcObject = stream;
      // Preview convention: a camera facing the operator is mirrored so
      // hand movements read intuitively; a rear camera is not. This is
      // display-only — the decode pipeline draws the unmirrored frames,
      // and an absent facing report means a desktop webcam facing the
      // operator.
      const diagnostics = diagnosticsRef.current;
      const extraStreams: MediaStream[] = [];
      let facing: 'environment' | 'user' =
        stream.getVideoTracks()[0]?.getSettings().facingMode === 'environment'
          ? 'environment'
          : 'user';
      const applyMirror = (): void => {
        video.style.transform = facing === 'environment' ? '' : 'scaleX(-1)';
      };
      applyMirror();
      await video.play();
      diagnostics.note(`receiver aperture opened, camera facing ${facing}`);
      switchCameraRef.current = async () => {
        const next = facing === 'environment' ? 'user' : 'environment';
        const swapped = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: next },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            frameRate: { ideal: 60 },
          },
        });
        if (stopped) {
          for (const track of swapped.getTracks()) track.stop();
          return;
        }
        extraStreams.push(swapped);
        for (const track of (video.srcObject as MediaStream | null)?.getVideoTracks() ?? []) {
          track.stop();
        }
        video.srcObject = swapped;
        facing = swapped.getVideoTracks()[0]?.getSettings().facingMode === 'environment'
          ? 'environment'
          : 'user';
        applyMirror();
        await video.play().catch(() => undefined);
        setCameraFacing(facing);
        diagnostics.note(`camera switched, now facing ${facing}`);
      };
      setCameraFacing(facing);

      let engine: ReceiverLiveSession | null = null;
      let returnBurstsPlayed = 0;
      let opticalBytesWindow = 0;
      let acousticBytesWindow = 0;
      let windowStartedAt = performance.now();
      let opticalRate = 0;
      let acousticRate = 0;
      let lastDecodeLatencyMs: number | null = null;
      let tracking = false;

      const forwardPlan = createForwardAudioPlan(48_000);
      const returnPlan = createReturnAudioPlan(48_000);

      const publish = (): void => {
        if (engine === null) return;
        const elapsedWindow = (performance.now() - windowStartedAt) / 1_000;
        if (elapsedWindow > 4) {
          opticalRate = opticalBytesWindow / elapsedWindow;
          acousticRate = acousticBytesWindow / elapsedWindow;
          opticalBytesWindow = 0;
          acousticBytesWindow = 0;
          windowStartedAt = performance.now();
        }
        setSnapshot(
          buildReceiverLiveSnapshot(engine, forwardPlan, {
            opticalBytesPerSecond: opticalRate,
            acousticBytesPerSecond: acousticRate,
            decoderLatencyMs: lastDecodeLatencyMs,
            cameraFps: null,
            displayCadenceHz: null,
            returnBurstsPlayed,
          }),
        );
      };

      const adoptBootstrap = async (offerBytes: Uint8Array): Promise<void> => {
        if (engine !== null || stopped) return;
        engine = await ReceiverLiveSession.accept(offerBytes, Date.now(), {
          forwardAudioDisabled: speakerPriority,
        });
        diagnostics.note(`bootstrap adopted, session ${engine.session.sessionId.slice(0, 8)}`);
        const adopted = engine;
        engineSummaryRef.current = () => ({
          phase: adopted.phase,
          phaseDetail: adopted.phaseDetail,
          ledger: { ...adopted.ledger, channels: undefined },
          channels: adopted.ledger.channels,
          integrityViolations: adopted.integrityViolations,
          transferAuthorised: adopted.transferAuthorised,
          speakerPriority,
        });
        setStatusDetail(
          'Session keys derived; answering over the return QR and speaker. Keep the raster inside the gate and this screen facing the sender camera.',
        );
        setState('session');
        // The return loop is the physical mirror of AudibleProbe: the
        // receiver's speaker becomes the feedback transmitter.
        void (async () => {
          while (!stopped && engine !== null) {
            let payload: Uint8Array | null;
            try {
              payload = await engine.nextReturnPayload();
            } catch {
              payload = null;
            }
            if (payload === null) {
              await sleep(800);
              continue;
            }
            // Both return media carry the identical bytes: the QR for the
            // sender's camera and the burst for the sender's microphone.
            setReturnQrText(encodeReturnText(payload));
            const playbackContext = audioGraph?.context ?? standalonePlayback;
            if (playbackContext !== null) {
              const transmission = synthesiseOfdmTransmission(payload, returnPlan);
              await playSamples(playbackContext, transmission.samples);
              returnBurstsPlayed += 1;
            }
            await sleep(engine.phase === 'COMPLETE' ? RETURN_COMPLETE_GAP_MS : RETURN_GAP_MS);
          }
        })();
        publish();
      };

      if (speakerPriority) {
        standalonePlayback = new AudioContext({ sampleRate: 48_000, latencyHint: 'interactive' });
        await standalonePlayback.resume();
        if (standalonePlayback.sampleRate !== 48_000) {
          throw new Error(
            `This device's audio engine runs at ${standalonePlayback.sampleRate} Hz, but the duplex carrier grid requires 48000 Hz.`,
          );
        }
      } else {
        audioGraph = await openCaptureAudioGraph(
          stream,
          'forward',
          'spectrabus-receiver-forward-capture',
          (payload) => {
            if (engine === null) {
              if (payload.length === SESSION_DISCOVERY_OFFER_BYTES) void adoptBootstrap(payload);
              return;
            }
            acousticBytesWindow += payload.length;
            void engine.acceptForwardPayload(payload, 'acoustic').then((events) => {
              for (const event of events) {
                if (event.kind !== 'equation-duplicate') {
                  diagnostics.note(`forward audio: ${event.kind} ${event.detail}`);
                }
              }
              publish();
            });
          },
        );
      }

      opticalWorker = new Worker(new URL('./optical-capture.worker.ts', import.meta.url), {
        name: 'spectrabus-receiver-optical-capture',
        type: 'module',
      });
      let opticalInFlight = false;
      let opticalSampleIntervalMs = OPTICAL_SAMPLE_COLD_MS;
      opticalWorker.onmessage = (event: MessageEvent<CaptureRuntimeEvent>) => {
        const message = event.data;
        if (message.type === 'optical-payload') {
          opticalInFlight = false;
          opticalSampleIntervalMs = OPTICAL_SAMPLE_TRACKED_MS;
          tracking = true;
          lastDecodeLatencyMs = message.decodeLatencyMs;
          const payload = new Uint8Array(message.payload);
          if (engine === null) {
            if (payload.length === SESSION_DISCOVERY_OFFER_BYTES) void adoptBootstrap(payload);
            return;
          }
          opticalBytesWindow += payload.length;
          void engine.acceptForwardPayload(payload, 'optical').then((events) => {
            for (const event of events) {
              if (event.kind !== 'equation-duplicate' && event.kind !== 'equation-admitted') {
                diagnostics.note(`forward optical: ${event.kind} ${event.detail}`);
              }
            }
            publish();
          });
          return;
        }
        if (message.type === 'optical-searching') {
          opticalInFlight = false;
          tracking = message.tracking;
          if (!message.tracking) opticalSampleIntervalMs = OPTICAL_SAMPLE_COLD_MS;
          // Live attempt evidence for the operator: a climbing count with no
          // lock means the raster is visible but unreadable (too small, too
          // far, glare), which is a different problem from no camera signal.
          if (engine === null) {
            setStatusDetail(
              `Aperture open; ${message.rejectedFrames} raster attempts without a lock — fill the alignment gate with the sender raster.`,
            );
          }
        }
      };

      const captureCanvas = document.createElement('canvas');
      const captureContext = captureCanvas.getContext('2d', { willReadFrequently: true });
      let lastOpticalSampleAt = 0;
      let lastQrScanAt = 0;
      let lastFrameAt = performance.now();
      let fpsEstimate: number | null = null;
      const detectorPromise = createQrDetector();
      let qrBusy = false;

      const onVideoFrame = (): void => {
        if (stopped) return;
        const now = performance.now();
        const delta = now - lastFrameAt;
        lastFrameAt = now;
        if (delta > 0 && delta < 1_000) {
          const instantaneous = 1_000 / delta;
          fpsEstimate = fpsEstimate === null ? instantaneous : fpsEstimate * 0.9 + instantaneous * 0.1;
          setCameraFps(fpsEstimate === null ? null : Math.round(fpsEstimate));
        }

        if (engine === null && now - lastQrScanAt > QR_SCAN_INTERVAL_MS && !qrBusy) {
          lastQrScanAt = now;
          qrBusy = true;
          void detectorPromise
            .then(async (detector) => {
              if (detector === null || stopped || engine !== null) return;
              const barcodes = await detector.detect(video);
              for (const barcode of barcodes) {
                const offerBytes = decodeBootstrapText(barcode.rawValue);
                if (offerBytes !== null) {
                  await adoptBootstrap(offerBytes);
                  break;
                }
              }
            })
            .catch(() => undefined)
            .finally(() => {
              qrBusy = false;
            });
        }

        if (
          captureContext !== null &&
          !opticalInFlight &&
          now - lastOpticalSampleAt > opticalSampleIntervalMs &&
          video.videoWidth > 0
        ) {
          lastOpticalSampleAt = now;
          opticalInFlight = true;
          const targetWidth = 720;
          const aspect = video.videoHeight / video.videoWidth;
          const targetHeight = Math.max(360, Math.min(540, Math.round(targetWidth * aspect)));
          captureCanvas.width = targetWidth;
          captureCanvas.height = targetHeight;
          captureContext.drawImage(video, 0, 0, targetWidth, targetHeight);
          const image = captureContext.getImageData(0, 0, targetWidth, targetHeight);
          const rgba = image.data.slice().buffer;
          const command: CaptureRuntimeCommand = {
            type: 'optical-frame',
            width: targetWidth,
            height: targetHeight,
            rgba,
          };
          opticalWorker?.postMessage(command, [rgba]);
        }

        scheduleVideoFrame();
      };
      const scheduleVideoFrame = (): void => {
        if (stopped) return;
        if ('requestVideoFrameCallback' in video) {
          video.requestVideoFrameCallback(onVideoFrame);
        } else {
          requestAnimationFrame(onVideoFrame);
        }
      };
      scheduleVideoFrame();

      timers.push(
        window.setInterval(() => {
          if (stopped) return;
          // Self-healing for any platform-initiated pause (tab switches,
          // DOM reparenting): a paused camera element stops delivering
          // video-frame callbacks and the whole optical pipeline with it.
          if (video.paused) void video.play().catch(() => undefined);
          engine?.tick();
          publish();
        }, SNAPSHOT_INTERVAL_MS),
      );

      if (openRunRef.current !== openRun) {
        stopped = true;
        for (const timer of timers) window.clearInterval(timer);
        opticalWorker?.terminate();
        audioGraph?.release();
        void standalonePlayback?.close().catch(() => undefined);
        for (const track of stream.getTracks()) track.stop();
        for (const extra of extraStreams) for (const track of extra.getTracks()) track.stop();
        video.srcObject = null;
        return;
      }
      const playbackToClose = standalonePlayback;
      releaseRef.current = () => {
        stopped = true;
        for (const timer of timers) window.clearInterval(timer);
        opticalWorker?.postMessage({ type: 'reset' } satisfies CaptureRuntimeCommand);
        opticalWorker?.terminate();
        audioGraph?.release();
        void playbackToClose?.close().catch(() => undefined);
        for (const track of stream?.getTracks() ?? []) track.stop();
        for (const extra of extraStreams) for (const track of extra.getTracks()) track.stop();
        video.srcObject = null;
      };
      setSpeakerPriority(speakerPriority);
      setVideoElement(video);
      setStatusDetail(
        tracking
          ? 'Aperture open; tracking the sender raster.'
          : 'Aperture open; searching for the sender bootstrap (QR or O1).',
      );
      setState('scanning');

      // A scan with a native camera application lands here with the offer
      // in the URL fragment; adopting it immediately means the return band
      // starts answering without any in-page optical acquisition, which is
      // the bootstrap path for platforms without a barcode API.
      const linkOffer = decodeBootstrapText(location.hash.slice(1));
      if (linkOffer !== null) {
        await adoptBootstrap(linkOffer);
      }
    } catch (error) {
      stopped = true;
      for (const timer of timers) window.clearInterval(timer);
      opticalWorker?.terminate();
      audioGraph?.release();
      void standalonePlayback?.close().catch(() => undefined);
      for (const track of stream?.getTracks() ?? []) track.stop();
      releaseRef.current = null;
      setFailureMessage(error instanceof Error ? error.message : 'The live receiver could not start.');
      setState('failed');
    }
  }, [enabled]);

  const switchCamera = useCallback(async () => {
    await switchCameraRef.current?.();
  }, []);

  const copyDiagnostics = useCallback(
    async () => diagnosticsRef.current.toClipboard('receiver', engineSummaryRef.current?.() ?? {}),
    [],
  );

  return {
    state,
    failureMessage,
    snapshot,
    statusDetail,
    cameraFps,
    videoElement,
    speakerPriority,
    returnQrText,
    cameraFacing,
    open,
    switchCamera,
    copyDiagnostics,
    reset,
  };
}
